import * as React from 'react';

import { enumBrokeredOrderStatus } from '@rematter/frontend-lib-api-client';
import { useToast } from '@rematter/ui-web/hooks';
import { cn } from '@rematter/ui-web/lib';
import { useSetAtom } from 'jotai';
import {
  CheckCircle,
  Download,
  FileText,
  PlayCircle,
  Printer,
  Trash2,
  Undo2,
} from 'lucide-react';
import { useLocation } from 'wouter';

import {
  $closeBrokeredOrder,
  $openBrokeredOrder,
} from '../../../_state/brokeredOrderDetail.state';
import { useTranslations } from '../../../i18n/useTranslations';
import {
  BrokeredOrderDetailRecord,
  BrokeredOrderRecord,
} from '../../../types/brokeredOrder';

export type LabelWithIconProps = {
  label: string;
  icon: React.ReactNode;
  className?: string;
};

export const LabelWithIcon = ({
  label,
  icon,
  className,
}: LabelWithIconProps) => (
  <div className={cn('@LabelWithIcon', 'flex items-center gap-2', className)}>
    {icon}
    {label}
  </div>
);

type UseBrokeredOrderMenuItemsProps = {
  order: BrokeredOrderRecord | BrokeredOrderDetailRecord | null;
  showVoidButton?: boolean;
  showFinishButton?: boolean;
  onVoid?: (id: string) => void;
  onFinish?: (id: string) => void;
  onRefetch?: () => void;
  displayListActions?: boolean;
};

export const useBrokeredOrderMenuItems = ({
  order,
  showVoidButton = false,
  showFinishButton = false,
  onVoid,
  onFinish,
  onRefetch,
  displayListActions = false,
}: UseBrokeredOrderMenuItemsProps) => {
  const toast = useToast();
  const t = useTranslations();
  const [, navigate] = useLocation();
  const openOrder = useSetAtom($openBrokeredOrder);
  const closeOrder = useSetAtom($closeBrokeredOrder);

  const hasPurchaseOrders = React.useMemo(
    () =>
      order && 'purchaseOrders' in order
        ? (order.purchaseOrders?.length ?? 0) > 0
        : false,
    [order],
  );

  const hasSalesOrders = React.useMemo(
    () =>
      order && 'salesOrders' in order
        ? (order.salesOrders?.length ?? 0) > 0
        : false,
    [order],
  );

  const isDraft = order?.status === enumBrokeredOrderStatus.Draft;
  const isOpen = order?.status === enumBrokeredOrderStatus.Open;
  const isInProgress = order?.status === enumBrokeredOrderStatus.InProgress;
  const isClosed = order?.status === enumBrokeredOrderStatus.Closed;
  const isFulfilled = order?.status === enumBrokeredOrderStatus.Fulfilled;
  const isVoided = order?.status === enumBrokeredOrderStatus.Voided;

  const canOpen = React.useMemo(() => isDraft, [isDraft]);

  const canVoid = React.useMemo(
    () => showVoidButton && (isDraft || isOpen || isInProgress),
    [showVoidButton, isDraft, isOpen, isInProgress],
  );

  const canFinish = React.useMemo(
    () => showFinishButton && isOpen,
    [showFinishButton, isOpen],
  );

  const canRevertToOpen = React.useMemo(
    () => displayListActions && (isClosed || isFulfilled || isVoided),
    [displayListActions, isClosed, isFulfilled, isVoided],
  );

  const showDocumentActions = React.useMemo(
    () =>
      isOpen ||
      isClosed ||
      isFulfilled ||
      (isVoided && (hasPurchaseOrders || hasSalesOrders)),
    [
      isOpen,
      isClosed,
      isFulfilled,
      isVoided,
      hasPurchaseOrders,
      hasSalesOrders,
    ],
  );

  const showPrintDownloadActions = React.useMemo(
    () => showDocumentActions && hasSalesOrders,
    [showDocumentActions, hasSalesOrders],
  );

  const showViewPurchaseOrder = React.useMemo(
    () => showDocumentActions && hasPurchaseOrders,
    [showDocumentActions, hasPurchaseOrders],
  );

  const showViewSalesOrder = React.useMemo(
    () => showDocumentActions && hasSalesOrders,
    [showDocumentActions, hasSalesOrders],
  );

  const handleOpen = React.useCallback(async () => {
    if (!order?.id) return;
    try {
      await openOrder({ id: order.id });
      toast.success(t.orders.toast.opened);
      onRefetch?.();
    } catch (err) {
      toast.error(
        t.orders.toast.openError(
          err instanceof Error ? err.message : t.common.errors.unknown,
        ),
      );
    }
  }, [order?.id, openOrder, toast, t, onRefetch]);

  const handleVoid = React.useCallback(() => {
    if (!order?.id) return;
    onVoid?.(order.id);
  }, [order?.id, onVoid]);

  const handleFinish = React.useCallback(async () => {
    if (!order?.id) return;
    if (onFinish) {
      onFinish(order.id);
    } else {
      try {
        await closeOrder({ id: order.id });
        toast.success(t.orders.toast.finished);
        onRefetch?.();
      } catch (err) {
        toast.error(
          t.orders.toast.finishError(
            err instanceof Error ? err.message : t.common.errors.unknown,
          ),
        );
      }
    }
  }, [order?.id, onFinish, closeOrder, toast, t, onRefetch]);

  const handleRevertToOpen = React.useCallback(async () => {
    if (!order?.id) return;
    try {
      await openOrder({ id: order.id });
      toast.success(t.orders.toast.reverted);
      onRefetch?.();
    } catch (err) {
      toast.error(
        t.orders.toast.revertError(
          err instanceof Error ? err.message : t.common.errors.unknown,
        ),
      );
    }
  }, [order?.id, openOrder, toast, t, onRefetch]);

  // TODO: implement actions logic based on documents from Brokered Order
  const handlePrintPackingLists = React.useCallback(() => {
    toast.info(t.orders.toast.comingSoon);
  }, [toast, t]);

  const handlePrintBillsOfLading = React.useCallback(() => {
    toast.info(t.orders.toast.comingSoon);
  }, [toast, t]);

  const handleDownloadPackingLists = React.useCallback(() => {
    toast.info(t.orders.toast.comingSoon);
  }, [toast, t]);

  const handleDownloadBillsOfLading = React.useCallback(() => {
    toast.info(t.orders.toast.comingSoon);
  }, [toast, t]);

  const handleViewPurchaseOrder = React.useCallback(() => {
    if (!order?.id) return;
    navigate(`/brokerage/orders/${order.id}?tab=orders`);
  }, [navigate, order?.id]);

  const handleViewSalesOrder = React.useCallback(() => {
    if (!order?.id) return;
    navigate(`/brokerage/orders/${order.id}?tab=orders`);
  }, [navigate, order?.id]);

  return React.useMemo(
    () => [
      ...(canOpen
        ? [
            {
              key: 'open',
              onAction: handleOpen,
              label: (
                <LabelWithIcon
                  label={t.orders.detail.actions.openOrder}
                  icon={<PlayCircle className="size-4" />}
                />
              ),
            },
          ]
        : []),
      ...(canFinish
        ? [
            {
              key: 'finish',
              onAction: handleFinish,
              label: (
                <LabelWithIcon
                  label={t.orders.detail.actions.finishOrder}
                  icon={<CheckCircle className="size-4" />}
                />
              ),
            },
          ]
        : []),
      ...(canRevertToOpen
        ? [
            {
              key: 'revert',
              onAction: handleRevertToOpen,
              label: (
                <LabelWithIcon
                  label={t.orders.detail.actions.revertToOpen}
                  icon={<Undo2 className="size-4" />}
                />
              ),
            },
          ]
        : []),
      ...(showPrintDownloadActions
        ? [
            {
              key: 'printPackingLists',
              onAction: handlePrintPackingLists,
              label: (
                <LabelWithIcon
                  label={t.orders.detail.actions.printPackingLists}
                  icon={<Printer className="size-4" />}
                />
              ),
            },
            {
              key: 'printBillsOfLading',
              onAction: handlePrintBillsOfLading,
              label: (
                <LabelWithIcon
                  label={t.orders.detail.actions.printBillsOfLading}
                  icon={<Printer className="size-4" />}
                />
              ),
            },
            {
              key: 'downloadPackingLists',
              onAction: handleDownloadPackingLists,
              label: (
                <LabelWithIcon
                  label={t.orders.detail.actions.downloadPackingLists}
                  icon={<Download className="size-4" />}
                />
              ),
            },
            {
              key: 'downloadBillsOfLading',
              onAction: handleDownloadBillsOfLading,
              label: (
                <LabelWithIcon
                  label={t.orders.detail.actions.downloadBillsOfLading}
                  icon={<Download className="size-4" />}
                />
              ),
            },
          ]
        : []),
      ...(showViewPurchaseOrder
        ? [
            {
              key: 'viewPurchaseOrder',
              onAction: handleViewPurchaseOrder,
              label: (
                <LabelWithIcon
                  label={t.orders.detail.actions.viewPurchaseOrder}
                  icon={<FileText className="size-4" />}
                />
              ),
            },
          ]
        : []),
      ...(showViewSalesOrder
        ? [
            {
              key: 'viewSalesOrder',
              onAction: handleViewSalesOrder,
              label: (
                <LabelWithIcon
                  label={t.orders.detail.actions.viewSalesOrder}
                  icon={<FileText className="size-4" />}
                />
              ),
            },
          ]
        : []),
      ...(canVoid
        ? [
            {
              key: 'void',
              onAction: handleVoid,
              label: (
                <LabelWithIcon
                  className="text-failure"
                  label={t.orders.detail.actions.voidOrder}
                  icon={<Trash2 className="size-4" />}
                />
              ),
            },
          ]
        : []),
    ],
    [
      t,
      canOpen,
      canVoid,
      canFinish,
      canRevertToOpen,
      showPrintDownloadActions,
      showViewPurchaseOrder,
      showViewSalesOrder,
      handleOpen,
      handleVoid,
      handleFinish,
      handleRevertToOpen,
      handlePrintPackingLists,
      handlePrintBillsOfLading,
      handleDownloadPackingLists,
      handleDownloadBillsOfLading,
      handleViewPurchaseOrder,
      handleViewSalesOrder,
    ],
  );
};
